import InventoryItem from '../domain/models/InventoryItem';
import InventoryAssignment from '../domain/models/InventoryAssignment';

export default class InventoryService {
  constructor(repository, studentService, auditLogger) {
    this.repository = repository;
    this.studentService = studentService;
    this.auditLogger = auditLogger;
  }

  list() {
    return this.repository.getAll().map((item) => new InventoryItem(item));
  }

  getById(id) {
    const raw = this.repository.getById(id);
    return raw ? new InventoryItem(raw) : null;
  }

  create(data, user) {
    const item = new InventoryItem(data);
    this.repository.create(item.toJSON());
    this.auditLogger?.log('inventory', 'Item created', { itemId: item.id }, user);
    return item;
  }

  update(id, data, user) {
    const existing = this.getById(id);
    if (!existing) return null;
    const updated = new InventoryItem({
      ...existing.toJSON(),
      ...data,
      id,
      updatedAt: new Date().toISOString()
    });
    this.repository.update(id, updated.toJSON());
    this.auditLogger?.log('inventory', 'Item updated', { itemId: id, changes: data }, user);
    return updated;
  }

  assignItem(id, assignmentData, user) {
    const existing = this.getById(id);
    if (!existing) return null;
    const assignment = new InventoryAssignment({
      ...assignmentData,
      checkoutDate: assignmentData.checkoutDate || new Date().toISOString().slice(0, 10)
    });
    const assignments = [assignment.toJSON(), ...existing.assignments.map((a) => a.toJSON())];
    const updated = this.update(
      id,
      {
        status: 'checked_out',
        assignments,
        updatedAt: new Date().toISOString()
      },
      user
    );
    if (assignment.assignedToType === 'student' && this.studentService) {
      this.studentService.update(assignment.assignedToId, { lastUpdated: new Date().toISOString() }, user);
    }
    this.auditLogger?.log('inventory', 'Item assigned', { itemId: id, assignmentId: assignment.id }, user);
    return updated;
  }

  checkInItem(id, assignmentId, user) {
    const existing = this.getById(id);
    if (!existing) return null;
    const assignments = existing.assignments.map((assignment) => {
      if (assignment.id !== assignmentId) return assignment.toJSON();
      return {
        ...assignment.toJSON(),
        checkinDate: new Date().toISOString().slice(0, 10)
      };
    });
    const hasActive = assignments.some((assignment) => !assignment.checkinDate);
    const updated = this.update(
      id,
      {
        status: hasActive ? 'checked_out' : 'available',
        assignments,
        updatedAt: new Date().toISOString()
      },
      user
    );
    this.auditLogger?.log('inventory', 'Item checked in', { itemId: id, assignmentId }, user);
    return updated;
  }

  getActiveAssignment(item) {
    return item.assignments.find((assignment) => assignment.isActive) || null;
  }

  getDueStatus(item) {
    const active = this.getActiveAssignment(item);
    if (!active || !active.dueDate) {
      return { isOverdue: false, isDueSoon: false, daysRemaining: null };
    }
    const due = new Date(active.dueDate);
    const today = new Date();
    const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    return {
      isOverdue: diffDays < 0,
      isDueSoon: diffDays >= 0 && diffDays <= 7,
      daysRemaining: diffDays
    };
  }

  getAssignmentsForStudent(studentId) {
    return this.list()
      .flatMap((item) =>
        item.assignments
          .filter((assignment) => assignment.assignedToType === 'student' && assignment.assignedToId === studentId)
          .map((assignment) => ({ item, assignment, due: this.getDueStatus(item) }))
      )
      .sort((a, b) => (a.assignment.checkoutDate < b.assignment.checkoutDate ? 1 : -1));
  }

  getAssignmentsForStaff(staffId) {
    return this.list()
      .flatMap((item) =>
        item.assignments
          .filter((assignment) => assignment.assignedToType === 'staff' && assignment.assignedToId === staffId)
          .map((assignment) => ({ item, assignment, due: this.getDueStatus(item) }))
      )
      .sort((a, b) => (a.assignment.checkoutDate < b.assignment.checkoutDate ? 1 : -1));
  }
}
